import type { AxiosError as ApiError } from "axios"
import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  IconButton,
  Input,
  Spinner,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query"
import { FiChevronDown, FiChevronRight, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi"

import {
  BudgetCategorys,
  BudgetCategoryTypes,
  ProviderClientTypes,
  Surveys,
  ServiceCatalog,
  type BudgetCategoryPublic,
  type BudgetSubCategoryPublic,
  type ServiceItemPublic,
  type ProviderClientTypePublic as ProviderClientType,
} from "@/client"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"

export const Route = createFileRoute("/_layout/provider/clienttypes")({
  component: ClientTypesPage,
})

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: "6px",
  border: "1px solid #e2e8f0",
  fontSize: "0.875rem",
  background: "white",
}

// ── Add/Edit dialog ───────────────────────────────────────────────────────────

const ClientTypeDialog = ({
  providerId,
  existing,
  budgetCategoryTypes,
  onClose,
}: {
  providerId: string
  existing?: ProviderClientType
  budgetCategoryTypes: Array<{ id: string; categoryType?: string | null }>
  onClose: () => void
}) => {
  const [name, setName] = useState(existing?.name ?? "")
  const [budgetCategoryTypeId, setBudgetCategoryTypeId] = useState(
    existing?.budgetCategoryTypeId ?? ""
  )
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const mutation = useMutation({
    mutationFn: () =>
      existing
        ? ProviderClientTypes.updateProviderClientType({
            path: { id: existing.id },
            body: {
              name,
              budgetCategoryTypeId: budgetCategoryTypeId || null,
            },
          })
        : ProviderClientTypes.createProviderClientType({
            body: {
              providerId,
              name,
              budgetCategoryTypeId: budgetCategoryTypeId || null,
            },
          }),
    onSuccess: () => {
      showSuccessToast(existing ? "Client type updated." : "Client type created.")
      queryClient.invalidateQueries({ queryKey: ["providerClientTypes", providerId] })
      onClose()
    },
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <VStack gap={4} align="stretch">
      <Field label="Name" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. NDIS Participant" />
      </Field>
      <Field label="Budget Category Type">
        <select
          style={selectStyle}
          value={budgetCategoryTypeId}
          onChange={(e) => setBudgetCategoryTypeId(e.target.value)}
        >
          <option value="">— None —</option>
          {budgetCategoryTypes.map((t) => (
            <option key={t.id} value={t.id}>{t.categoryType}</option>
          ))}
        </select>
      </Field>
      <Flex justify="flex-end" gap={2} pt={2}>
        <Button variant="subtle" colorPalette="gray" onClick={onClose}>Cancel</Button>
        <Button
          loading={mutation.isPending}
          disabled={!name.trim()}
          onClick={() => mutation.mutate()}
        >
          {existing ? "Save" : "Create"}
        </Button>
      </Flex>
    </VStack>
  )
}

// ── Service defaults panel for one client type ────────────────────────────────

const ServiceDefaultsPanel = ({
  clientType,
  providerId,
}: {
  clientType: ProviderClientType
  providerId: string
}) => {
  const queryClient = useQueryClient()

  const { data: categoriesData, isLoading: loadingCats } = useQuery({
    queryKey: ["budgetCategories"],
    queryFn: () => BudgetCategorys.readBudgetCategorys(),
    enabled: !!providerId,
  })
  const categories: BudgetCategoryPublic[] = categoriesData?.data ?? []

  const subCategoryQueries = useQueries({
    queries: categories.map((cat) => ({
      queryKey: ["budgetSubCategories", providerId, cat.id],
      queryFn: () => ServiceCatalog.listSubcategoriesByProvider({ path: { providerId }, query: { budgetCategoryId: cat.id } }),
      enabled: !!cat.id,
    })),
  })

  const allSubs: BudgetSubCategoryPublic[] = subCategoryQueries.flatMap((q) => q.data?.data ?? [])

  const itemQueries = useQueries({
    queries: allSubs.map((sub) => ({
      queryKey: ["serviceItems", sub.id],
      queryFn: () => ServiceCatalog.listItemsBySubcategory({ path: { subCategoryId: sub.id } }),
      enabled: !!sub.id,
    })),
  })

  const { data: defaults, isLoading: loadingDefaults } = useQuery({
    queryKey: ["clientTypeDefaults", clientType.id],
    queryFn: () => ProviderClientTypes.listServiceDefaults({ path: { id: clientType.id } }),
  })
  const defaultSet = new Map((defaults ?? []).map((d) => [d.serviceItemId, d.id]))

  const addMutation = useMutation({
    mutationFn: (serviceItemId: string) =>
      ProviderClientTypes.addServiceDefault({ path: { id: clientType.id }, query: { serviceItemId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientTypeDefaults", clientType.id] })
    },
    onError: (err: ApiError) => handleError(err),
  })

  const removeMutation = useMutation({
    mutationFn: (defaultId: string) =>
      ProviderClientTypes.removeServiceDefault({ path: { id: clientType.id, defaultId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientTypeDefaults", clientType.id] })
    },
    onError: (err: ApiError) => handleError(err),
  })

  const toggle = (serviceItemId: string, isOn: boolean) => {
    if (isOn) {
      const dId = defaultSet.get(serviceItemId)
      if (dId) removeMutation.mutate(dId)
    } else {
      addMutation.mutate(serviceItemId)
    }
  }

  const isPending = addMutation.isPending || removeMutation.isPending
  const isLoading =
    loadingCats ||
    loadingDefaults ||
    subCategoryQueries.some((q) => q.isLoading) ||
    itemQueries.some((q) => q.isLoading)

  // Build items per subcategory
  const subItemMap = new Map<string, ServiceItemPublic[]>()
  allSubs.forEach((sub, subIdx) => {
    subItemMap.set(sub.id, itemQueries[subIdx]?.data?.data ?? [])
  })

  if (isLoading) return <Spinner size="sm" m={2} />

  return (
    <VStack align="stretch" gap={4} pt={2}>
      {categories.map((cat, catIdx) => {
        const subs = subCategoryQueries[catIdx]?.data?.data ?? []
        if (subs.length === 0) return null
        return (
          <Box key={cat.id}>
            <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2} textTransform="uppercase">
              {cat.category}
            </Text>
            {subs.map((sub) => {
              const items = subItemMap.get(sub.id) ?? []
              if (items.length === 0) return null
              return (
                <Box key={sub.id} mb={2} pl={2}>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1}>
                    {sub.name}
                  </Text>
                  <Flex gap={2} flexWrap="wrap">
                    {items.map((item) => {
                      const on = defaultSet.has(item.id)
                      return (
                        <Switch.Root
                          key={item.id}
                          checked={on}
                          disabled={isPending}
                          onCheckedChange={() => toggle(item.id, on)}
                        >
                          <Switch.HiddenInput />
                          <Switch.Control />
                          <Switch.Label fontSize="sm">{item.name}</Switch.Label>
                        </Switch.Root>
                      )
                    })}
                  </Flex>
                </Box>
              )
            })}
          </Box>
        )
      })}
    </VStack>
  )
}

// ── Survey associations panel ─────────────────────────────────────────────────

const SurveysPanel = ({
  clientType,
  providerId,
}: {
  clientType: ProviderClientType
  providerId: string
}) => {
  const queryClient = useQueryClient()

  const { data: surveysData, isLoading: loadingSurveys } = useQuery({
    queryKey: ["surveys", providerId],
    queryFn: () => Surveys.readSurveys({ query: { providerId: providerId || undefined } }),
    enabled: !!providerId,
  })

  const { data: assigned, isLoading: loadingAssigned } = useQuery({
    queryKey: ["clientTypeSurveys", clientType.id],
    queryFn: () => ProviderClientTypes.listClientTypeSurveys({ path: { id: clientType.id } }),
  })

  const assignedSet = new Set((assigned ?? []).map((a) => a.surveyId))

  const add = useMutation({
    mutationFn: (surveyId: string) =>
      ProviderClientTypes.addClientTypeSurvey({ path: { id: clientType.id, surveyId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["clientTypeSurveys", clientType.id] }),
    onError: (err: ApiError) => handleError(err),
  })

  const remove = useMutation({
    mutationFn: (surveyId: string) =>
      ProviderClientTypes.removeClientTypeSurvey({ path: { id: clientType.id, surveyId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["clientTypeSurveys", clientType.id] }),
    onError: (err: ApiError) => handleError(err),
  })

  const isPending = add.isPending || remove.isPending
  const isLoading = loadingSurveys || loadingAssigned

  if (isLoading) return <Spinner size="sm" m={2} />

  const surveys = surveysData?.data ?? []
  const globalSurveys = surveys.filter((s) => s.isGlobal)
  const providerSurveys = surveys.filter((s) => !s.isGlobal)

  const renderGroup = (label: string, items: typeof surveys) => {
    if (items.length === 0) return null
    return (
      <Box key={label}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1} textTransform="uppercase">
          {label}
        </Text>
        <Flex gap={2} flexWrap="wrap">
          {items.map((s) => {
            const on = assignedSet.has(s.id)
            return (
              <Switch.Root
                key={s.id}
                checked={on}
                disabled={isPending}
                onCheckedChange={() => on ? remove.mutate(s.id) : add.mutate(s.id)}
              >
                <Switch.HiddenInput />
                <Switch.Control />
                <Switch.Label fontSize="sm">{s.title || "Untitled"}</Switch.Label>
              </Switch.Root>
            )
          })}
        </Flex>
      </Box>
    )
  }

  if (surveys.length === 0) {
    return (
      <Text fontSize="sm" color="gray.400">No surveys available. Create surveys in the Surveys section first.</Text>
    )
  }

  return (
    <VStack align="stretch" gap={4} pt={2}>
      {renderGroup("Global Surveys", globalSurveys)}
      {renderGroup("Provider Surveys", providerSurveys)}
    </VStack>
  )
}

// ── Client type row (expandable) ──────────────────────────────────────────────

const ClientTypeRow = ({
  clientType,
  providerId,
  budgetCategoryTypes,
}: {
  clientType: ProviderClientType
  providerId: string
  budgetCategoryTypes: Array<{ id: string; categoryType?: string | null }>
}) => {
  const [expanded, setExpanded] = useState(false)
  const [activeTab, setActiveTab] = useState<"services" | "surveys">("services")
  const [editOpen, setEditOpen] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const deleteMutation = useMutation({
    mutationFn: () => ProviderClientTypes.deleteProviderClientType({ path: { id: clientType.id } }),
    onSuccess: () => {
      showSuccessToast("Client type deleted.")
      queryClient.invalidateQueries({ queryKey: ["providerClientTypes", providerId] })
    },
    onError: (err: ApiError) => handleError(err),
  })

  const bct = budgetCategoryTypes.find((t) => t.id === clientType.budgetCategoryTypeId)

  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden">
      <Flex
        align="center"
        gap={2}
        px={4}
        py={3}
        cursor="pointer"
        _hover={{ bg: "gray.50" }}
        onClick={() => setExpanded((x) => !x)}
      >
        {expanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
        <Text fontWeight="medium" flex={1}>{clientType.name}</Text>
        {bct && (
          <Badge colorPalette="blue" variant="subtle" size="sm" mr={2}>
            {bct.categoryType}
          </Badge>
        )}
        <IconButton
          aria-label="Edit"
          size="xs"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); setEditOpen(true) }}
        >
          <FiEdit2 />
        </IconButton>
        <IconButton
          aria-label="Delete"
          size="xs"
          variant="ghost"
          colorPalette="red"
          loading={deleteMutation.isPending}
          onClick={(e) => { e.stopPropagation(); deleteMutation.mutate() }}
        >
          <FiTrash2 />
        </IconButton>
      </Flex>

      {expanded && (
        <Box px={4} pb={4} borderTopWidth="1px" borderColor="gray.100" bg="gray.50">
          {/* Tab bar */}
          <Flex gap={0} mt={3} mb={3} borderBottomWidth="1px" borderColor="gray.200">
            {(["services", "surveys"] as const).map((tab) => (
              <Box
                key={tab}
                px={3} py={2}
                fontSize="sm"
                fontWeight={activeTab === tab ? "semibold" : "normal"}
                color={activeTab === tab ? "blue.600" : "gray.500"}
                borderBottomWidth="2px"
                borderColor={activeTab === tab ? "blue.500" : "transparent"}
                cursor="pointer"
                onClick={() => setActiveTab(tab)}
                textTransform="capitalize"
              >
                {tab === "services" ? "Default Service Types" : "Surveys"}
              </Box>
            ))}
          </Flex>

          {activeTab === "services" && (
            <ServiceDefaultsPanel clientType={clientType} providerId={providerId} />
          )}
          {activeTab === "surveys" && (
            <SurveysPanel clientType={clientType} providerId={providerId} />
          )}
        </Box>
      )}

      <DialogRoot
        open={editOpen}
        onOpenChange={({ open }) => setEditOpen(open)}
        size={{ base: "xs", md: "md" }}
        placement="center"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Client Type</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <ClientTypeDialog
              providerId={providerId}
              existing={clientType}
              budgetCategoryTypes={budgetCategoryTypes}
              onClose={() => setEditOpen(false)}
            />
          </DialogBody>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

function ClientTypesPage() {
  const providerId = sessionStorage.getItem("selectedProviderId") ?? ""
  const [createOpen, setCreateOpen] = useState(false)

  const { data: typesData, isLoading } = useQuery({
    queryKey: ["providerClientTypes", providerId],
    queryFn: () => ProviderClientTypes.listProviderClientTypes({ query: { providerId } }),
    enabled: !!providerId,
  })

  const { data: bctData } = useQuery({
    queryKey: ["budgetCategoryTypes"],
    queryFn: () => BudgetCategoryTypes.listBudgetCategoryTypes({ query: { provider_id: providerId } }),
  })

  const clientTypes = typesData?.data ?? []
  const budgetCategoryTypes = bctData?.data ?? []

  return (
    <Container maxW="full">
      <Heading size="lg" pt={12} mb={2}>Client Types</Heading>
      <Text color="gray.500" fontSize="sm" mb={6}>
        Define client types (e.g. NDIS, HCP Level 1) with linked budget category types and default allowed services.
      </Text>

      <Flex mb={4}>
        <DialogRoot
          open={createOpen}
          onOpenChange={({ open }) => setCreateOpen(open)}
          size={{ base: "xs", md: "md" }}
          placement="center"
        >
          <DialogTrigger asChild>
            <Button size="sm">
              <FiPlus />
              New Client Type
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New Client Type</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <ClientTypeDialog
                providerId={providerId}
                budgetCategoryTypes={budgetCategoryTypes}
                onClose={() => setCreateOpen(false)}
              />
            </DialogBody>
            <DialogCloseTrigger />
          </DialogContent>
        </DialogRoot>
      </Flex>

      {isLoading && <Spinner />}

      {!isLoading && clientTypes.length === 0 && (
        <Text color="gray.400">No client types yet. Create one to get started.</Text>
      )}

      <VStack gap={2} align="stretch">
        {clientTypes.map((ct) => (
          <ClientTypeRow
            key={ct.id}
            clientType={ct}
            providerId={providerId}
            budgetCategoryTypes={budgetCategoryTypes}
          />
        ))}
      </VStack>
    </Container>
  )
}
